import React from 'react';
import { Trip } from '../hooks/useTrips';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { MoreVertical, Copy, Trash2 } from 'lucide-react';

function formatTripDates(startDate: string, endDate: string): string {
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');

  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

  if (start.getFullYear() !== end.getFullYear()) {
    return `${start.toLocaleDateString('en-US', { ...options, year: 'numeric' })} - ${end.toLocaleDateString('en-US', { ...options, year: 'numeric' })}`;
  }

  if (start.getMonth() === end.getMonth()) {
    return `${start.toLocaleDateString('en-US', { month: 'short' })} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
  }

  return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', { ...options, year: 'numeric' })}`;
}

interface TripCardProps {
  trip: Trip;
  onSelect: () => void;
  onDelete?: (() => void) | null;
  onShare?: (() => void) | null;
  onClone?: (() => void) | null;
  tripId?: string;
  copiedTripId?: string | null;
  /** isNext: adds gold left-border accent for the "next upcoming" trip */
  isNext?: boolean;
  /** Suppress dark-mode ring when wrapped in SwipeableCard (which owns the ring) */
  noRing?: boolean;
}

export function TripCard({
  trip,
  onSelect,
  onDelete,
  onShare,
  onClone,
  tripId,
  copiedTripId,
  isNext = false,
  noRing = false,
}: TripCardProps) {

  return (
    <article className={`relative w-full bg-white dark:bg-surface-dark-card rounded-xl p-5 shadow-card hover:shadow-card-hover transition-all duration-200 text-left${noRing ? '' : ' dark:ring-1 dark:ring-white/[0.06]'}${isNext ? ' border-l-4 border-disney-gold-400' : ''}`} onClick={onSelect}>
      {/* Floating share button (visible on mobile) */}
      {onShare && (
        <button
          onClick={(e) => { e.stopPropagation(); onShare(); }}
          className="absolute top-2 right-2 md:hidden p-1.5 rounded-lg text-gray-400 hover:text-disney-purple-600 dark:hover:text-disney-purple-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          aria-label="Share trip"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
        </button>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-900 dark:text-gray-100">{trip.name}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {formatTripDates(trip.startDate, trip.endDate)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isNext && trip.daysUntilStart > 0 && (
            <span className="text-sm font-medium text-disney-gold-600 dark:text-disney-gold-400">
              {trip.daysUntilStart} days away
            </span>
          )}
          {isNext && trip.daysUntilStart === 0 && (
            <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">Active</span>
          )}
          {!isNext && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {trip.visitCount} visit{trip.visitCount !== 1 ? 's' : ''}
            </span>
          )}
          {tripId !== undefined && copiedTripId === tripId && (
            <span className="text-xs text-disney-green-600 dark:text-disney-green-400 animate-fade-in">
              Copied!
            </span>
          )}
          <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </article>
  );
}
